/**
 * Loyalty and referral hooks. Single request per domain (main APIs only: loyalty, referral).
 */
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../shared/auth/store';
import { isLoyaltyFeatureEnabled } from '../config';
import { fetchLoyaltyData, fetchReferralData, type RewardItem } from './api';
import { loyaltyQueryKey, referralQueryKey } from './keys';

export function useLoyalty() {
  const customer_id = useAuthStore(s => s.user?.id);
  const loyalty_enabled = isLoyaltyFeatureEnabled();
  const query = useQuery({
    queryKey: [...loyaltyQueryKey, customer_id],
    queryFn: () => fetchLoyaltyData(customer_id),
    enabled: loyalty_enabled && typeof customer_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
  const loyaltyData = query.data;
  return {
    data: loyaltyData?.summary ?? null,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
  };
}

export function usePointsHistory() {
  const customer_id = useAuthStore(s => s.user?.id);
  const loyalty_enabled = isLoyaltyFeatureEnabled();
  const query = useQuery({
    queryKey: [...loyaltyQueryKey, customer_id],
    queryFn: () => fetchLoyaltyData(customer_id),
    enabled: loyalty_enabled && typeof customer_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
  const data = query.data?.pointsHistory ?? [];
  return {
    data,
    isLoading: query.isLoading,
    fetchNextPage: () => {},
    hasNextPage: false,
    isFetchingNextPage: false,
  };
}

export function useRewards() {
  const customer_id = useAuthStore(s => s.user?.id);
  const loyalty_enabled = isLoyaltyFeatureEnabled();
  const query = useQuery({
    queryKey: [...loyaltyQueryKey, customer_id],
    queryFn: () => fetchLoyaltyData(customer_id),
    enabled: loyalty_enabled && typeof customer_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
  return {
    data: (query.data?.rewards ?? []) as RewardItem[],
    isLoading: query.isLoading,
  };
}

export function useReferralHistory() {
  const customer_id = useAuthStore(s => s.user?.id);
  const query = useQuery({
    queryKey: [...referralQueryKey, customer_id],
    queryFn: () => fetchReferralData(customer_id!),
    enabled: typeof customer_id === 'number',
    staleTime: 1000 * 60 * 2,
  });
  return {
    data: query.data?.referralHistory ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}

/**
 * Referrals available for checkout (from main referral API).
 */
export function useReferrals() {
  const customer_id = useAuthStore(s => s.user?.id);
  const query = useQuery({
    queryKey: [...referralQueryKey, customer_id],
    queryFn: () => fetchReferralData(customer_id!),
    enabled: typeof customer_id === 'number',
    staleTime: 1000 * 60,
  });
  return {
    data: query.data?.referrals ?? [],
    isLoading: query.isLoading,
    refetch: query.refetch,
  };
}
